import * as yup from "yup";
import { Form } from "@/components/organisms/Form";
import { request } from "@/common/api";
import { useForm } from "react-hook-form";
import { Buttons } from "@/components/atoms/Buttons";
import { TelControl } from "@/components/containers/Controls/TelControl";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextControl } from "@/components/containers/Controls/TextControl";
import { BaseResponse } from "@/common/types/baseRespones";
import { FormInputBox } from "@/components/molecules/FormInputBox";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { useJoinFormHandlers } from "@/common/hooks/useJoinFormHandlers";
import { ReactComponent as IconArrow } from "@/styles/assets/svg/icon_arrow.svg";

const schema = yup
  .object({
    guardian: yup
      .object({
        name: yup
          .string()
          .nullable()
          .required("보호자 이름은 필수 입력입니다."),
        birth: yup
          .date()
          .typeError(
            "생년월일은 YYYY-MM-DD 형식이어야 하며, 유효한 날짜여야 합니다."
          )
          .required("생년월일은 필수 입력입니다."),
        tel: yup
          .string()
          .length(
            13,
            "연락처는 000-0000-0000 형식이며, 13자에 맞게 작성하셔야 합니다."
          )
          .required("연락처는 필수 입력입니다."),
      })
      .required("보호자 정보는 필수 입력입니다."),
  })
  .required();

export const GuardianInfoEditPage = () => {
  const navigate = useNavigate();
  const { guardian, handleNumberChange, handleNameChange, handleBirthChange } =
    useJoinFormHandlers();

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      guardian: {
        name: guardian.name || "",
        birth: guardian.birth ? new Date(guardian.birth) : new Date(),
        tel: guardian.tel || "",
      },
    },
  });

  const { mutateAsync: updateInfo } = useMutation({
    mutationFn: (data: {
      id: string;
      name: string;
      birth: Date;
      tel: string;
    }) => {
      return request<BaseResponse>({
        method: "PUT",
        url: "/guardian",
        data: data,
      });
    },
  });

  const onSubmit = async (data: any) => {
    try {
      await updateInfo({
        id: guardian.id,
        name: data.guardian.name,
        birth: data.guardian.birth,
        tel: data.guardian.tel,
      });
      navigate("/setting/user");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <MainTemplate
      nav={false}
      subPage={true}
      menuBtn={false}
      buttonLeft={
        <Buttons
          type="button"
          size="headerTextIcon"
          color="primary100"
          layout="text"
          label="뒤로"
          icon={<IconArrow />}
          onClick={() => window.history.back()}
        />
      }
      pageTitle="내 정보 수정"
    >
      <Form
        submitBtn={
          <Buttons
            type="submit"
            size="lg"
            color="primary100"
            layout="solid"
            label="수정하기"
            disabled={false}
            onClick={handleSubmit(onSubmit)}
          />
        }
        backBtn={
          <Buttons
            type="button"
            size="lg"
            color="primary100"
            layout="text"
            label="취소"
            disabled={false}
            onClick={() => window.history.back()}
          />
        }
      >
        <FormInputBox formTitle="법정보호자 정보 수정">
          <>
            <TextControl
              control={control}
              name="guardian.name"
              label="법정보호자 이름"
              placeholder="법정보호자 분의 성함을 입력하세요."
              required={true}
              value={guardian.name}
              onChange={(e) => handleNameChange(e, "guardian")}
            />
            <TextControl
              type="date"
              control={control}
              name="guardian.birth"
              label="법정보호자 생년월일"
              placeholder="0000-00-00"
              required={true}
              value={
                guardian.birth
                  ? new Date(guardian.birth).toISOString().split("T")[0]
                  : ""
              }
              onChange={(e) => handleBirthChange(e, "guardian")}
            />
            <TelControl
              type="tel"
              control={control}
              name="guardian.tel"
              label="법정보호자 연락처"
              placeholder="000-0000-0000"
              required={true}
              value={guardian.tel}
              onChange={(e) => handleNumberChange(e, "guardian")}
            />
          </>
        </FormInputBox>
      </Form>
    </MainTemplate>
  );
};
